<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { Info, BarChart3, Calendar, CheckCircle, Clock, AlertTriangle } from 'lucide-vue-next'
import AppDialog from '@/components/atoms/dialogs/AppDialog.vue'
import { useIndexedDB, type CalendarEvent } from '@/composables/useIndexedDB'
import Chart from 'primevue/chart'
import AppButton from '@/components/atoms/buttons/AppButton.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const showLegend = ref(false)
const showIndicators = ref(false)
const { initDB, getAllEvents } = useIndexedDB()
const events = ref<CalendarEvent[]>([])

const COLORS = {
  status: {
    done: '#8696fe',
    postponed: '#334257',
    expired: '#9ba4b5',
  },
  priority: {
    high: ['#dc2626', '#ed2b2a'],
    medium: ['#f97316', '#ff8400'],
    low: ['#22c55e'],
  },
}

const normalizedColor = (color?: string) => color?.toLowerCase() || ''
const hasColor = (event: CalendarEvent, colors: string[]) =>
  colors.map((color) => color.toLowerCase()).includes(normalizedColor(event.color))
const isDone = (event: CalendarEvent) => normalizedColor(event.color) === COLORS.status.done
const isPostponed = (event: CalendarEvent) =>
  normalizedColor(event.color) === COLORS.status.postponed
const isExpired = (event: CalendarEvent) => {
  if (normalizedColor(event.color) === COLORS.status.expired) return true
  if (isDone(event) || isPostponed(event)) return false

  return new Date(event.end).getTime() < Date.now()
}

const total = computed(() => events.value.length)
const callsDone = computed(() => events.value.filter(isDone).length)
const postponed = computed(() => events.value.filter(isPostponed).length)
const expired = computed(() => events.value.filter(isExpired).length)
const pending = computed(() =>
  Math.max(total.value - callsDone.value - postponed.value - expired.value, 0),
)
const high = computed(() => events.value.filter((e) => hasColor(e, COLORS.priority.high)).length)
const medium = computed(
  () => events.value.filter((e) => hasColor(e, COLORS.priority.medium)).length,
)
const low = computed(() => events.value.filter((e) => hasColor(e, COLORS.priority.low)).length)

const hasData = computed(() => total.value > 0)
const completionRate = computed(() =>
  total.value > 0 ? Math.round((callsDone.value / total.value) * 100) : 0,
)

const statusChartData = computed(() => ({
  labels: [
    t('calendar.calls_done'),
    t('calendar.postponed'),
    t('calendar.expired'),
    t('calendar.pending'),
  ],
  datasets: [
    {
      data: [callsDone.value, postponed.value, expired.value, pending.value],
      backgroundColor: [
        COLORS.status.done,
        COLORS.status.postponed,
        COLORS.status.expired,
        '#22c55e',
      ],
    },
  ],
}))

const priorityChartData = computed(() => ({
  labels: [t('calendar.priority.high'), t('calendar.priority.medium'), t('calendar.priority.low')],
  datasets: [
    {
      data: [high.value, medium.value, low.value],
      backgroundColor: ['#dc2626', '#f97316', '#22c55e'],
    },
  ],
}))

const chartOptions = {
  plugins: { legend: { display: false } },
  cutout: '65%',
}

const refreshEvents = async () => {
  await initDB()
  events.value = await getAllEvents()
}

const openLegend = async () => {
  await refreshEvents()
  showLegend.value = true
}

const openIndicators = async () => {
  await refreshEvents()
  showIndicators.value = true
  showLegend.value = false
}

onMounted(async () => {
  await refreshEvents()
})
</script>

<template>
  <AppButton
    v-tooltip.bottom="$t('calendar.info_calendar')"
    size="small"
    rounded
    :block="false"
    @click="openLegend"
    class="!w-10 !h-10 !p-2 flex-shrink-0 flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95"
  >
    <template #icon-start>
      <Info :stroke-width="2" />
    </template>
  </AppButton>

  <AppDialog
    v-model="showLegend"
    :header="$t('calendar.info_calendar')"
    :style="{ width: '420px' }"
    custom-class="backdrop-blur-xl!"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <div>
          <h2 class="font-semibold text-[var(--p-surface-color)]">
            {{ $t('calendar.info_calendar') }}
          </h2>
          <p class="text-sm text-[var(--p-surface-color)]">
            {{ $t('calendar.color_convention') }}
          </p>
        </div>
      </div>
    </template>

    <template #content>
      <div class="calendar-info-dialog space-y-6">
        <div class="p-4 bg-[var(--p-surface-50)]/20 dark:bg-[var(--p-surface-800)]/20 rounded-lg shadow-xs">
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <Calendar class="w-4 h-4 text-[var(--p-surface-600)]" />
              <span class="text-sm font-medium">{{ $t('calendar.total_number_events') }}</span>
            </div>
            <span class="text-lg font-semibold">{{ total }}</span>
          </div>

          <div class="grid grid-cols-2 gap-2">
            <div class="text-center p-2 bg-white/30 dark:bg-[var(--p-surface-700)]/20 rounded">
              <div class="text-sm font-medium text-green-600">{{ completionRate }}%</div>
              <div class="text-xs text-[var(--p-surface-color)]">
                {{ $t('calendar.completed_events') }}
              </div>
            </div>
            <div class="text-center p-2 bg-white/30 dark:bg-[var(--p-surface-700)]/20 rounded">
              <div class="text-sm font-medium text-red-600">{{ high }}</div>
              <div class="text-xs text-[var(--p-surface-color)]">
                {{ $t('calendar.priority_high') }}
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 class="text-sm font-medium text-[var(--p-surface-color)] mb-3">
            {{ $t('calendar.event_priority') }}
          </h3>
          <div class="space-y-2">
            <div
              class="flex items-center justify-between px-3 py-2 bg-white/20 dark:bg-[var(--p-surface-700)]/20 shadow-xs rounded-xl"
            >
              <div class="flex items-center gap-3">
                <div class="w-3 h-3 rounded-full bg-red-500"></div>
                <span class="text-sm">{{ $t('calendar.priority.high') }}</span>
              </div>
              <span class="text-sm font-medium">{{ high }}</span>
            </div>

            <div
              class="flex items-center justify-between px-3 py-2 bg-white/20 dark:bg-[var(--p-surface-700)]/20 shadow-xs rounded-xl"
            >
              <div class="flex items-center gap-3">
                <div class="w-3 h-3 rounded-full bg-orange-500"></div>
                <span class="text-sm">{{ $t('calendar.priority.medium') }}</span>
              </div>
              <span class="text-sm font-medium">{{ medium }}</span>
            </div>

            <div
              class="flex items-center justify-between px-3 py-2 bg-white/20 dark:bg-[var(--p-surface-700)]/20 shadow-xs rounded-xl"
            >
              <div class="flex items-center gap-3">
                <div class="w-3 h-3 rounded-full bg-green-500"></div>
                <span class="text-sm">{{ $t('calendar.priority.low') }}</span>
              </div>
              <span class="text-sm font-medium">{{ low }}</span>
            </div>
          </div>
        </div>

        <div>
          <h3 class="text-sm font-medium text-[var(--p-surface-color)] mb-3">
            {{ $t('calendar.status') }}
          </h3>
          <div class="space-y-2">
            <div
              class="flex items-center justify-between px-3 py-2 bg-white/20 dark:bg-[var(--p-surface-700)]/20 shadow-xs rounded-xl"
            >
              <div class="flex items-center gap-3">
                <div class="w-3 h-3 rounded-full bg-indigo-500"></div>
                <span class="text-sm">{{ $t('calendar.call_done') }}</span>
              </div>
              <span class="text-sm font-medium">{{ callsDone }}</span>
            </div>

            <div
              class="flex items-center justify-between px-3 py-2 bg-white/20 dark:bg-[var(--p-surface-700)]/20 shadow-xs rounded-xl"
            >
              <div class="flex items-center gap-3">
                <div class="w-3 h-3 rounded-full bg-gray-600"></div>
                <span class="text-sm">{{ $t('calendar.postponed_call') }}</span>
              </div>
              <span class="text-sm font-medium">{{ postponed }}</span>
            </div>

            <div
              class="flex items-center justify-between px-3 py-2 bg-white/20 dark:bg-[var(--p-surface-700)]/20 shadow-xs rounded-xl"
            >
              <div class="flex items-center gap-3">
                <div class="w-3 h-3 rounded-full bg-gray-400"></div>
                <span class="text-sm">{{ $t('calendar.expired_event') }}</span>
              </div>
              <span class="text-sm font-medium">{{ expired }}</span>
            </div>

            <div
              class="flex items-center justify-between px-3 py-2 bg-white/20 dark:bg-[var(--p-surface-700)]/20 shadow-xs rounded-xl"
            >
              <div class="flex items-center gap-3">
                <div class="w-3 h-3 rounded-full bg-green-500"></div>
                <span class="text-sm">{{ $t('calendar.pending') }}</span>
              </div>
              <span class="text-sm font-medium">{{ pending }}</span>
            </div>
          </div>
        </div>
        <AppButton @click="openIndicators" :label="$t('calendar.view_indicators')" />
      </div>
    </template>
  </AppDialog>

  <AppDialog
    v-model="showIndicators"
    :header="$t('calendar.info_calendar')"
    :style="{ width: '500px' }"
    class="backdrop-blur-xl!"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <div>
          <h2 class="font-semibold text-[var(--p-surface-color)]">
            {{ $t('calendar.indicators') }}
          </h2>
          <p class="text-sm text-[var(--p-surface-color)]">
            {{ $t('calendar.statistics_calendar') }}
          </p>
        </div>
      </div>
    </template>
    <template #content>
      <div class="calendar-info-dialog space-y-2 mt-2">
        <div class="grid grid-cols-3 gap-3">
          <div class="text-center p-3 bg-white/20 dark:bg-[var(--p-surface-700)]/20 rounded-lg shadow-xs">
            <div class="text-xl font-bold">{{ total }}</div>
            <div class="text-xs text-[var(--p-surface-color)] mt-1">{{ $t('calendar.total') }}</div>
          </div>

          <div class="text-center p-3 bg-white/20 dark:bg-[var(--p-surface-700)]/20 rounded-lg shadow-xs">
            <div class="text-xl font-bold text-green-600">{{ callsDone }}</div>
            <div class="text-xs text-[var(--p-surface-color)] mt-1">
              {{ $t('calendar.completed') }}
            </div>
          </div>

          <div class="text-center p-3 bg-white/20 dark:bg-[var(--p-surface-700)]/20 rounded-lg shadow-xs">
            <div class="text-xl font-bold text-red-600">{{ high }}</div>
            <div class="text-xs text-[var(--p-surface-color)] mt-1">
              {{ $t('calendar.urgent') }}
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div class="p-4 bg-white/20 dark:bg-[var(--p-surface-700)]/20 shadow-xs rounded-lg">
            <h3 class="text-sm font-medium text-[var(--p-surface-color)] mb-3">
              {{ $t('calendar.by_state') }}
            </h3>

            <div v-if="!hasData" class="text-center py-4">
              <BarChart3 class="w-8 h-8 mx-auto text-gray-300 mb-2" />
              <p class="text-sm text-[var(--p-surface-color)]">{{ $t('calendar.no_data') }}</p>
            </div>

            <div v-else>
              <div class="flex justify-center mb-3">
                <Chart
                  type="doughnut"
                  :data="statusChartData"
                  :options="chartOptions"
                  class="w-32 h-32"
                />
              </div>

              <div class="space-y-1">
                <div class="flex items-center justify-between text-sm">
                  <div class="flex items-center gap-2">
                    <div class="w-2 h-2 rounded-full bg-indigo-500"></div>
                    <span>{{ $t('calendar.calls_done') }}</span>
                  </div>
                  <div class="font-medium">{{ callsDone }}</div>
                </div>
                <div class="flex items-center justify-between text-sm">
                  <div class="flex items-center gap-2">
                    <div class="w-2 h-2 rounded-full bg-gray-600"></div>
                    <span>{{ $t('calendar.postponed') }}</span>
                  </div>
                  <div class="font-medium">{{ postponed }}</div>
                </div>
                <div class="flex items-center justify-between text-sm">
                  <div class="flex items-center gap-2">
                    <div class="w-2 h-2 rounded-full bg-gray-400"></div>
                    <span>{{ $t('calendar.expired') }}</span>
                  </div>
                  <div class="font-medium">{{ expired }}</div>
                </div>
                <div class="flex items-center justify-between text-sm">
                  <div class="flex items-center gap-2">
                    <div class="w-2 h-2 rounded-full bg-green-500"></div>
                    <span>{{ $t('calendar.pending') }}</span>
                  </div>
                  <div class="font-medium">{{ pending }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="p-4 bg-white/20 dark:bg-[var(--p-surface-700)]/20 shadow-xs rounded-lg">
            <h3 class="text-sm font-medium text-[var(--p-surface-color)] mb-3">
              {{ $t('calendar.by_priority') }}
            </h3>

            <div v-if="!hasData" class="text-center py-4">
              <AlertTriangle class="w-8 h-8 mx-auto text-gray-300 mb-2" />
              <p class="text-sm text-[var(--p-surface-color)]">{{ $t('calendar.no_data') }}</p>
            </div>

            <div v-else>
              <div class="flex justify-center mb-3">
                <Chart
                  type="doughnut"
                  :data="priorityChartData"
                  :options="chartOptions"
                  class="w-32 h-32"
                />
              </div>

              <div class="space-y-1">
                <div class="flex items-center justify-between text-sm">
                  <div class="flex items-center gap-2">
                    <div class="w-2 h-2 rounded-full bg-red-500"></div>
                    <span>{{ $t('calendar.priority.high') }}</span>
                  </div>
                  <div class="font-medium">{{ high }}</div>
                </div>
                <div class="flex items-center justify-between text-sm">
                  <div class="flex items-center gap-2">
                    <div class="w-2 h-2 rounded-full bg-orange-500"></div>
                    <span>{{ $t('calendar.priority.medium') }}</span>
                  </div>
                  <div class="font-medium">{{ medium }}</div>
                </div>
                <div class="flex items-center justify-between text-sm">
                  <div class="flex items-center gap-2">
                    <div class="w-2 h-2 rounded-full bg-green-500"></div>
                    <span>{{ $t('calendar.priority.low') }}</span>
                  </div>
                  <div class="font-medium">{{ low }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="p-4 bg-[var(--p-surface-0)]/50 dark:bg-[var(--p-surface-700)]/20 rounded-lg">
          <h3 class="text-sm font-medium text-[var(--p-surface-color)] mb-2">
            {{ $t('calendar.summary') }}
          </h3>
          <div class="space-y-1">
            <div class="flex items-center justify-between text-sm">
              <div class="flex items-center gap-2">
                <CheckCircle class="w-4 h-4 text-green-500" />
                <span>{{ $t('calendar.completion_rate') }}</span>
              </div>
              <span class="font-medium">{{ completionRate }}%</span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <div class="flex items-center gap-2">
                <Clock class="w-4 h-4 text-[var(--p-surface-color)]" />
                <span>{{ $t('calendar.pending') }}</span>
              </div>
              <span class="font-medium">{{ pending }}</span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <div class="flex items-center gap-2">
                <AlertTriangle class="w-4 h-4 text-red-500" />
                <span>{{ $t('calendar.for_attention') }}</span>
              </div>
              <span class="font-medium">{{ total - callsDone }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-start gap-2 mt-4">
        <AppButton
          @click="((showLegend = true), (showIndicators = false))"
          :label="$t('calendar.back_to_legend')"
        />
      </div>
    </template>
  </AppDialog>
</template>

<style scoped>
:global(.dark) .calendar-info-dialog {
  color: var(--p-surface-100);
}

:global(.dark) .calendar-info-dialog :is(h2, h3, span, .font-medium, .font-semibold, .font-bold) {
  color: var(--p-surface-100);
}

:global(.dark) .calendar-info-dialog p,
:global(.dark) .calendar-info-dialog .text-\[var\(--p-surface-color\)\],
:global(.dark) .calendar-info-dialog .text-\[var\(--p-surface-600\)\] {
  color: var(--p-surface-300) !important;
}

:global(.dark) .calendar-info-dialog :is(.bg-white, .bg-gray-50) {
  background-color: var(--p-surface-800) !important;
  border-color: var(--p-surface-700) !important;
}

:global(.dark) .calendar-info-dialog .bg-\[var\(--p-surface-50\)\] {
  background-color: var(--p-surface-800) !important;
  border: 1px solid var(--p-surface-700);
}

:global(.dark) .calendar-info-dialog .bg-\[var\(--p-primary-100\)\] {
  background-color: color-mix(in srgb, var(--p-primary-color) 22%, var(--p-surface-900)) !important;
}

:global(.dark) .calendar-info-dialog .text-\[var\(--p-primary-600\)\] {
  color: var(--p-primary-300) !important;
}

:global(.dark) .calendar-info-dialog .bg-\[var\(--p-primary-50\)\] {
  background-color: color-mix(in srgb, var(--p-primary-color) 16%, var(--p-surface-900)) !important;
  border: 1px solid color-mix(in srgb, var(--p-primary-color) 24%, var(--p-surface-700));
}

:global(.dark) .calendar-info-dialog .border {
  border-color: var(--p-surface-700) !important;
}

:global(.dark) .calendar-info-dialog .text-gray-300 {
  color: var(--p-surface-500) !important;
}

:global(.dark) .calendar-info-dialog .text-gray-600 {
  color: var(--p-surface-300) !important;
}
</style>
